import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { dt, CHANNEL_LABELS, FOLLOWUP_LABELS, FOLLOWUP_COLORS } from "@/lib/format";
import { PageHeader, Loader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Repeat, X, Search } from "lucide-react";
import { toast } from "sonner";

export default function Followups() {
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState("todos");
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const debounceRef = useRef(null);

  const load = async () => {
    const params = {};
    if (filter !== "todos") params.estado = filter;
    if (qDebounced.trim()) params.q = qDebounced.trim();
    const r = await api.get("/seguimientos", { params });
    setList(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, qDebounced]);
  useEffect(() => { api.post("/notifications/marcar-leidas", null, { params: { tipo: "seguimiento_fallido" } }); }, []);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQDebounced(q), 350);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const cancelar = async (fu) => {
    try { await api.post(`/seguimientos/${fu.id}/cancelar`); toast.success("Seguimiento cancelado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto">
      <PageHeader title="Seguimientos" subtitle="Recuperá a quien preguntó y no confirmó su pedido." />

      <div className="ms-card p-3 mb-4 flex flex-wrap gap-3">
        <div className="relative w-56">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o teléfono…"
            className="rounded-xl pl-9 pr-8" data-testid="search-followups" />
          {q && (
            <button onClick={() => setQ("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="clear-search-followups">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 rounded-xl" data-testid="filter-seguimiento-estado"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            {Object.entries(FOLLOWUP_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!list ? <Loader /> : list.length === 0 ? (
        qDebounced
          ? <EmptyState icon={Repeat} title="Sin resultados" subtitle={`No encontramos seguimientos para «${qDebounced}».`} />
          : <EmptyState icon={Repeat} title="Sin seguimientos" subtitle="Cuando el agente muestre productos sin cerrar la venta, va a aparecer acá." />
      ) : (
        <div className="space-y-2">
          {list.map((f) => (
            <div key={f.id} data-testid={`followup-${f.id}`} className="ms-card p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{f.contacto_nombre || "Cliente"}</span>
                  <Badge className="rounded-full border-0 bg-accent text-xs">{CHANNEL_LABELS[f.canal] || f.canal}</Badge>
                  <Badge className={`rounded-full border-0 text-xs ${FOLLOWUP_COLORS[f.estado]}`}>{FOLLOWUP_LABELS[f.estado]}</Badge>
                  <span className="text-xs text-muted-foreground">#{f.numero}</span>
                </div>
                {f.mensaje && <p className="text-sm text-muted-foreground mt-1 truncate">{f.mensaje}</p>}
                <p className="text-[11px] text-muted-foreground/70 mt-0.5">
                  {f.estado === "pendiente" ? `Programado: ${dt(f.programado_para)}` : f.enviado_en ? `Enviado: ${dt(f.enviado_en)}` : dt(f.created_at)}
                </p>
                {/* Fix O (2026-09-24): seguimientos que no salieron porque el canal estaba en "Solo recibir" */}
                {f.motivo_cancelacion === "canal_solo_recibir" && (
                  <p className="text-[11px] text-amber-600 mt-0.5" data-testid={`followup-solo-recibir-${f.id}`}>
                    No se envió: el canal estaba en «Solo recibir» (Canales).
                  </p>
                )}
                {/* Fix P (2026-10-01): la tienda contestó desde el celular (WhatsApp con coexistencia) */}
                {f.motivo_cancelacion === "respondio_desde_el_celular" && (
                  <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`followup-celular-${f.id}`}>
                    No se envió: la tienda ya le respondió desde el celular.
                  </p>
                )}
              </div>
              {f.estado === "pendiente" && (
                <Button size="sm" variant="outline" className="rounded-full text-red-500 border-red-200 gap-1" onClick={() => cancelar(f)} data-testid={`cancel-followup-${f.id}`}>
                  <X className="h-4 w-4" /> Cancelar
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
